"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useStore } from "jotai";
import type { DiscoveryNavLinkView } from "@repo/sanity-utils/discoveryChrome";
import { DiscoveryHeaderAuth, DiscoveryHeaderAccountMobileLinks, type DiscoveryHeaderAuthUser } from "@repo/ui/aops-components/DiscoveryHeaderAuth";
import { selectedCampusAtom } from "@/app/atoms/courseSelections";
import { campusIdForAccountMenu, resolveAccountMenuLinks } from "@/lib/resolveAccountMenuLinks";

/**
 * The course and program pages write `selectedCampusAtom` during render
 * (`useHydrateAtoms` and a follow-up `store.set`). `useAtomValue` would
 * re-render this header in the middle of that render. Read the atom after
 * the write instead, and keep later picker updates off the render stack.
 *
 * This is a targeted workaround. The cleaner long-term fix would be to stop
 * LearningFormat and the program page from writing shared atoms during render;
 * then this hook could return to useAtomValue.
 */
function useSelectedCampusId() {
  const $ = _c(3);
  const store = useStore();
  const [selectedCampusId, setSelectedCampusId] = useState(null);
  let t0;
  let t1;
  if ($[0] !== store) {
    t0 = () => {
      const publish = () => {
        const next = store.get(selectedCampusAtom);
        setSelectedCampusId(current => current === next ? current : next);
      };
      publish();
      return store.sub(selectedCampusAtom, () => {
        queueMicrotask(publish);
      });
    };
    t1 = [store];
    $[0] = store;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return selectedCampusId;
}
function useResolvedAccountMenuLinks(links, serverCampusId) {
  const $ = _c(4);
  const selectedCampusId = useSelectedCampusId();
  let t0;
  if ($[0] !== links || $[1] !== selectedCampusId || $[2] !== serverCampusId) {
    t0 = resolveAccountMenuLinks(links, campusIdForAccountMenu(selectedCampusId, serverCampusId));
    $[0] = links;
    $[1] = selectedCampusId;
    $[2] = serverCampusId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
type AccountProps = {
  user: DiscoveryHeaderAuthUser | null;
  /** Unresolved Sanity links. `{{campus_id}}` is replaced as the campus changes. */
  links: DiscoveryNavLinkView[];
  serverCampusId: string | null;
  parentPortalHref: string;
  loginPath: string;
  logoutPath: string;
  signUpHref: string;
  redirectOrigin: string;
};
export function DiscoveryHeaderAuthWithCampus(t0) {
  const $ = _c(17);
  const {
    user,
    links,
    serverCampusId,
    parentPortalHref,
    loginPath,
    logoutPath,
    signUpHref,
    redirectOrigin
  } = t0;
  if (!user || !user.isParent && !user.isStudent) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = [];
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== loginPath || $[2] !== logoutPath || $[3] !== parentPortalHref || $[4] !== redirectOrigin || $[5] !== signUpHref || $[6] !== user) {
      t2 = <DiscoveryHeaderAuth user={user} accountLinks={t1} parentPortalHref={parentPortalHref} loginPath={loginPath} logoutPath={logoutPath} signUpHref={signUpHref} redirectOrigin={redirectOrigin} />;
      $[1] = loginPath;
      $[2] = logoutPath;
      $[3] = parentPortalHref;
      $[4] = redirectOrigin;
      $[5] = signUpHref;
      $[6] = user;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== links || $[9] !== loginPath || $[10] !== logoutPath || $[11] !== parentPortalHref || $[12] !== redirectOrigin || $[13] !== serverCampusId || $[14] !== signUpHref || $[15] !== user) {
    t1 = <AccountHeaderAuth user={user} links={links} serverCampusId={serverCampusId} parentPortalHref={parentPortalHref} loginPath={loginPath} logoutPath={logoutPath} signUpHref={signUpHref} redirectOrigin={redirectOrigin} />;
    $[8] = links;
    $[9] = loginPath;
    $[10] = logoutPath;
    $[11] = parentPortalHref;
    $[12] = redirectOrigin;
    $[13] = serverCampusId;
    $[14] = signUpHref;
    $[15] = user;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  return t1;
}
function AccountHeaderAuth(t0) {
  const $ = _c(8);
  const {
    user,
    links,
    serverCampusId,
    parentPortalHref,
    loginPath,
    logoutPath,
    signUpHref,
    redirectOrigin
  } = t0;
  const accountLinks = useResolvedAccountMenuLinks(links, serverCampusId);
  let t1;
  if ($[0] !== accountLinks || $[1] !== loginPath || $[2] !== logoutPath || $[3] !== parentPortalHref || $[4] !== redirectOrigin || $[5] !== signUpHref || $[6] !== user) {
    t1 = <DiscoveryHeaderAuth user={user} accountLinks={accountLinks} parentPortalHref={parentPortalHref} loginPath={loginPath} logoutPath={logoutPath} signUpHref={signUpHref} redirectOrigin={redirectOrigin} />;
    $[0] = accountLinks;
    $[1] = loginPath;
    $[2] = logoutPath;
    $[3] = parentPortalHref;
    $[4] = redirectOrigin;
    $[5] = signUpHref;
    $[6] = user;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
export function DiscoveryHeaderAccountMobileLinksWithCampus(t0) {
  const $ = _c(4);
  const {
    user,
    links,
    serverCampusId,
    logoutPath
  } = t0;
  const accountLinks = useResolvedAccountMenuLinks(links, serverCampusId);
  let t1;
  if ($[0] !== accountLinks || $[1] !== logoutPath || $[2] !== user) {
    t1 = <DiscoveryHeaderAccountMobileLinks user={user} links={accountLinks} logoutPath={logoutPath} />;
    $[0] = accountLinks;
    $[1] = logoutPath;
    $[2] = user;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}